import { Fragment } from "react";
import { Link, useLocation } from "react-router-dom";
import SharedNavbar from "../../components/feature/SharedNavbar";
import SharedFooter from "../../components/feature/SharedFooter";
import { ArticleHero, BlogArticleSchema, Para, SectionHeading, SourcesBox, TableOfContents, inlineLink } from "../../components/feature/BlogArticleKit";
import { SCHEDULED_ARTICLES, scheduledArticleGraph } from "../../seo/blog/scheduledArticles";
import { cmsScheduledArticle, cmsScheduledGraph } from "../../cms/records";

// Only our authored emphasis and links are interpreted; arbitrary HTML stays text.
function InlineText({ text }: { text: string }) {
  return <>{text.split(/(\*\*[^*]+\*\*|\[[^\]]+\]\(\/[a-z0-9/-]+\))/g).map((part, i) => {
    if (part.startsWith("**")) return <strong key={i} className="font-semibold text-gray-800">{part.slice(2, -2)}</strong>;
    const link = part.match(/^\[([^\]]+)\]\((\/[^)]+)\)$/);
    if (link) return <Link key={i} to={link[2]} className={inlineLink}>{link[1]}</Link>;
    return <Fragment key={i}>{part}</Fragment>;
  })}</>;
}

export default function ScheduledBlogPage() {
  const { pathname } = useLocation();
  const compiled = SCHEDULED_ARTICLES.find(a => a.route === pathname.replace(/\/$/, ""));
  if (!compiled) return null;
  // Content Studio edits (src/cms/records.ts); the same object when nothing is published.
  const article = cmsScheduledArticle(compiled);
  const graph = cmsScheduledGraph(scheduledArticleGraph(article.route), compiled)!;
  return <main className="min-h-screen bg-white">
    <BlogArticleSchema route={article.route} graph={graph} />
    <SharedNavbar />
    <ArticleHero breadcrumb={article.title} chips={["ESA Essentials", new Date(`${article.datePublished}T12:00:00Z`).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric", timeZone: "UTC" }), "PawTenant Editorial"]}
      h1={article.title} standfirst={article.description} image={article.image} imageAlt={article.imageAlt} imageWidth={1200} imageHeight={800} />
    <article data-pt-scheduled-article={article.route} className="max-w-3xl mx-auto px-5 sm:px-6 pb-12">
      <TableOfContents items={article.sections.map((s, i) => ({ id: `section-${i + 1}`, label: s.heading }))} />
      {/* data-cms-slot="article": the Content Studio preview swaps this element's
          children for a draft's rendered blocks (template "blog", scripts/cms/inventory.mjs). */}
      <div data-pt-article-body data-cms-slot="article">
        {article.sections.map((section, i) => <section key={section.heading}>
          <SectionHeading id={`section-${i + 1}`}>{section.heading}</SectionHeading>
          {section.paragraphs.map((text, j) => <Para key={j}><InlineText text={text} /></Para>)}
        </section>)}
      </div>
      <SourcesBox items={article.sources.map(s => <a key={s.url} className={inlineLink} href={s.url} target="_blank" rel="noopener noreferrer">{s.title}</a>)} />
      <p className="text-xs text-gray-500 leading-relaxed mt-6">General educational information, not individual medical or legal advice. Sources reviewed {article.datePublished}.</p>
    </article>
    <SharedFooter />
  </main>;
}
